import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} SubscriptionVariantPickerRefs
 * @property {HTMLElement[]} [supplyButtons]
 * @property {HTMLInputElement[]} [purchaseRadios]
 */

/**
 * Variant + subscription picker.
 * - Drives the hidden `variant-picker` by selecting a variant by id.
 * - Writes the selected selling plan id into all matching product forms via `input[name="selling_plan"]`.
 * - Replaces itself on `variant:update` using the HTML returned by the theme's variant-picker fetch.
 *
 * @extends {Component<SubscriptionVariantPickerRefs>}
 */
class SubscriptionVariantPicker extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Ensure inputs match initial checked state (subscription by default)
    this.#syncSellingPlanInputs();
    this.#syncPriceDisplays();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles supply tile clicks. Updates the hidden variant picker.
   * @param {Event} event
   */
  handleSupplyClick(event) {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target.closest('[data-variant-id]') : null);
    if (!button) return;
    const variantId = button.getAttribute('data-variant-id') || '';
    const optionValueId = button.getAttribute('data-option-value-id') || '';
    if (!variantId) return;

    const root = this.closest('.shopify-section, dialog') ?? document;
    const variantPicker =
      root.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ??
      document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!(variantPicker instanceof HTMLElement)) return;

    // Prefer radio inputs (buttons/swatches) and fall back to selects (dropdown style).
    const input = variantPicker.querySelector(`input[data-variant-id="${CSS.escape(variantId)}"]`);
    if (input instanceof HTMLInputElement) {
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // Fallback: select by option value id if the picker doesn't expose data-variant-id.
    if (optionValueId) {
      const inputByOptionValue = variantPicker.querySelector(`input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      if (inputByOptionValue instanceof HTMLInputElement) {
        if (!inputByOptionValue.checked) {
          inputByOptionValue.checked = true;
          inputByOptionValue.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const optionByOptionValue = variantPicker.querySelector(
        `select option[data-option-value-id="${CSS.escape(optionValueId)}"]`
      );
      if (optionByOptionValue instanceof HTMLOptionElement && optionByOptionValue.parentElement instanceof HTMLSelectElement) {
        const select = optionByOptionValue.parentElement;
        if (select.value !== optionByOptionValue.value) {
          select.value = optionByOptionValue.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }

    const option = variantPicker.querySelector(`select option[data-variant-id="${CSS.escape(variantId)}"]`);
    if (option instanceof HTMLOptionElement && option.parentElement instanceof HTMLSelectElement) {
      const select = option.parentElement;
      if (select.value !== option.value) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Handles purchase option changes. Syncs the `selling_plan` hidden input(s).
   * @param {Event} _event
   */
  handlePurchaseChange(_event) {
    this.#syncSellingPlanInputs();
    this.#syncPriceDisplays();
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail?.data?.productId && this.dataset.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const source = event.detail?.data?.html;
    if (!source) return;

    const newPicker = source.querySelector(
      `subscription-variant-picker[data-block-id="${CSS.escape(this.dataset.blockId || '')}"]`
    );
    if (!newPicker) return;

    this.replaceWith(newPicker);
  };

  #getSelectedSellingPlanId() {
    const checked = this.querySelector('input[type="radio"][data-radio-type]:checked');
    if (!(checked instanceof HTMLInputElement)) return '';

    const radioType = checked.dataset.radioType || '';
    if (radioType !== 'selling_plan') return '';

    return checked.dataset.sellingPlanId || '';
  }

  #getSelectedDisplayData() {
    const checked = this.querySelector('input[type="radio"][data-radio-type]:checked');
    if (!(checked instanceof HTMLInputElement)) return { price: '', discount: 0 };

    const price = checked.dataset.displayPrice || '';
    const discountRaw = checked.dataset.displayDiscount || '0';
    const discount = Number.parseInt(discountRaw, 10);
    return { price, discount: Number.isNaN(discount) ? 0 : discount };
  }

  #syncSellingPlanInputs() {
    const section = this.closest('.shopify-section, dialog') ?? document;
    const productId = this.dataset.productId || '';
    if (!productId) return;

    const planId = this.#getSelectedSellingPlanId();
    const forms = Array.from(
      section.querySelectorAll(`product-form-component[data-product-id="${CSS.escape(productId)}"] form`)
    );

    for (const form of forms) {
      const input = form.querySelector('input[name="selling_plan"]');
      if (input instanceof HTMLInputElement) {
        input.value = planId;
      }
    }
  }

  #syncPriceDisplays() {
    const section = this.closest('.shopify-section, dialog') ?? document;
    const productId = this.dataset.productId || '';
    if (!productId) return;

    const { price, discount } = this.#getSelectedDisplayData();
    if (!price) return;

    const productPrices = Array.from(section.querySelectorAll(`product-price[data-product-id="${CSS.escape(productId)}"]`));
    for (const productPriceEl of productPrices) {
      const priceContainer = productPriceEl.querySelector('[ref="priceContainer"]');
      if (!(priceContainer instanceof HTMLElement)) continue;

      // Skip volume pricing/ranges to avoid conflicting UI.
      if (priceContainer.querySelector('.price-range')) continue;

      const priceEls = Array.from(priceContainer.querySelectorAll('span.price'))
        .filter((el) => el instanceof HTMLElement)
        .filter((el) => !el.classList.contains('price-discount-badge'))
        .filter((el) => !el.classList.contains('price-range'));

      for (const el of priceEls) {
        el.textContent = price;
      }

      const badge = priceContainer.querySelector('.price-discount-badge');
      if (badge instanceof HTMLElement) {
        if (discount > 0) {
          badge.textContent = `${discount}% OFF`;
        } else {
          badge.remove();
        }
      }
    }
  }
}

if (!customElements.get('subscription-variant-picker')) {
  customElements.define('subscription-variant-picker', SubscriptionVariantPicker);
}

